/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetPlatformMCPOnboardingRequest,
  GetPlatformMCPOnboardingSecurity,
} from "../models/operations/getplatformmcponboarding.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildPlatformMCPOnboardingQuery,
  PlatformMCPOnboardingQueryData,
  prefetchPlatformMCPOnboarding,
  queryKeyPlatformMCPOnboarding,
} from "./platformMCPOnboarding.core.js";
export {
  buildPlatformMCPOnboardingQuery,
  type PlatformMCPOnboardingQueryData,
  prefetchPlatformMCPOnboarding,
  queryKeyPlatformMCPOnboarding,
};

export type PlatformMCPOnboardingQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getOnboarding platformMcp
 *
 * @remarks
 * Get the current user's safe Platform MCP onboarding projection for the active organization.
 */
export function usePlatformMCPOnboarding(
  request?: GetPlatformMCPOnboardingRequest | undefined,
  security?: GetPlatformMCPOnboardingSecurity | undefined,
  options?: QueryHookOptions<
    PlatformMCPOnboardingQueryData,
    PlatformMCPOnboardingQueryError
  >,
): UseQueryResult<
  PlatformMCPOnboardingQueryData,
  PlatformMCPOnboardingQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildPlatformMCPOnboardingQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getOnboarding platformMcp
 *
 * @remarks
 * Get the current user's safe Platform MCP onboarding projection for the active organization.
 */
export function usePlatformMCPOnboardingSuspense(
  request?: GetPlatformMCPOnboardingRequest | undefined,
  security?: GetPlatformMCPOnboardingSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    PlatformMCPOnboardingQueryData,
    PlatformMCPOnboardingQueryError
  >,
): UseSuspenseQueryResult<
  PlatformMCPOnboardingQueryData,
  PlatformMCPOnboardingQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildPlatformMCPOnboardingQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setPlatformMCPOnboardingData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: PlatformMCPOnboardingQueryData,
): PlatformMCPOnboardingQueryData | undefined {
  const key = queryKeyPlatformMCPOnboarding(...queryKeyBase);

  return client.setQueryData<PlatformMCPOnboardingQueryData>(key, data);
}

export function invalidatePlatformMCPOnboarding(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "platformMcp", "getOnboarding", ...queryKeyBase],
  });
}

export function invalidateAllPlatformMCPOnboarding(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "platformMcp", "getOnboarding"],
  });
}
